/*!
 * snice v7.10.0
 * A decorator-driven web component library with routing, controllers, daemons, and 130+ UI components. For better coding-agent results, run npx snice init-ai.
 * (c) 2024
 * Released under the MIT License.
 *
 * GENERATED FILE — DO NOT EDIT. Source: src/. Rebuild: npm run build:core
 */
// Global namespace for Snice framework
// Ensures all registries and symbols are shared across different JS files/modules
// Initialize the global Snice namespace on globalThis
// This ensures all module instances share the same registry
if (!globalThis.snice) {
    globalThis.snice = {
        controllerRegistry: new Map(),
        pendingControllerAttachments: new Map(),
        controllerIdCounter: 0
    };
}
// Multiple copies of Snice can share a global created by an older build.
globalThis.snice.pendingControllerAttachments ??= new Map();
// Helper function to get or create a global symbol
// Symbol.for() already guarantees global uniqueness, no need for a Map cache
function getSymbol(name) {
    return Symbol.for(`snice:${name}`);
}

// Central file for all symbols used in the framework
// All symbols are stored globally to ensure consistency across modules
const IS_CONTROLLER_CLASS = getSymbol('is-controller-class');
const IS_CONTROLLER_INSTANCE = getSymbol('is-controller-instance');
const IS_DAEMON_CLASS = getSymbol('is-daemon-class');
const IS_DAEMON_INSTANCE = getSymbol('is-daemon-instance');
const IS_ELEMENT_CLASS = getSymbol('is-element-class');
const CHANNEL_HANDLERS = getSymbol('channel-handlers');
// Per-instance daemon communication state. Daemon instances are constructed by
// application code; Snice only creates and manages their private EventTarget
// when an app context explicitly provides them.
const DAEMON_STATE = getSymbol('daemon-state');
// Internal element state symbols
const READY_PROMISE = getSymbol('ready-promise');
const READY_RESOLVE = getSymbol('ready-resolve');
const READY_REJECT = getSymbol('ready-reject');
// True only while an element is executing its own @ready handlers. A
// controller attached to that same host must not await the promise whose
// settlement depends on the current handler returning.
const READY_HANDLERS_RUNNING = getSymbol('ready-handlers-running');
const RENDERED_PROMISE = getSymbol('rendered-promise');
const RENDERED_RESOLVE = getSymbol('rendered-resolve');
// Set when a render is requested but dropped because the element was
// disconnected; consumed on reconnect to replay exactly the missed render.
const PENDING_RECONNECT_RENDER = getSymbol('pending-reconnect-render');
// Per-element synchronous render-nesting depth, used to cap runaway re-entry
// without a shared global counter that one element could exhaust for others.
const RENDER_DEPTH = getSymbol('render-depth');
const RENDER_ROOT = getSymbol('render-root');
const ELEMENT_OPTIONS = getSymbol('element-options');
const CONTROLLER = getSymbol('controller');
const INITIALIZED = getSymbol('initialized');
// Event handler symbols
const ON_HANDLERS = getSymbol('on-handlers');
// Controller symbols
const CONTROLLER_KEY = getSymbol('controller-key');
const CONTROLLER_NAME_KEY = getSymbol('controller-name');
const CONTROLLER_ID = getSymbol('controller-id');
const CONTROLLER_OPERATIONS = getSymbol('controller-operations');
const NATIVE_CONTROLLER = getSymbol('native-controller');
// Decorator name stored on the class so direct class bindings can expose the
// same useful DOM label without resolving the controller through the registry.
const CONTROLLER_REGISTRY_NAME = getSymbol('controller-registry-name');
// Set while a controller CLASS (not a registry name) is attached — the
// attribute/MutationObserver channel must not attach over or detach it.
const DIRECT_CONTROLLER = getSymbol('direct-controller');
// Guards framework-authored diagnostic controller attribute writes from the
// synchronous custom-element attributeChangedCallback path.
const CONTROLLER_ATTRIBUTE_SYNC = getSymbol('controller-attribute-sync');
// A controller class bound by a template before the custom element was
// defined/upgraded. Picked up by connectedCallback on first initialization.
const PENDING_CONTROLLER_BINDING = getSymbol('pending-controller-binding');
// Cleanup symbol - holds an object with all cleanup arrays
const CLEANUP = getSymbol('cleanup');
// Property symbols
const PROPERTIES = getSymbol('properties');
const PROPERTY_VALUES = getSymbol('property-values');
const PROPERTY_DEFAULTS = getSymbol('property-defaults');
const PROPERTY_WRAPPERS = getSymbol('property-wrappers');
const PRE_INIT_PROPERTY_VALUES = getSymbol('pre-init-property-values');
const PRE_UPGRADE_PROPERTY_BINDINGS = getSymbol('pre-upgrade-property-bindings');
const PROPERTIES_INITIALIZED = getSymbol('properties-initialized');
const PROPERTY_WATCHERS = getSymbol('property-watchers');
const EXPLICITLY_SET_PROPERTIES = getSymbol('explicitly-set-properties');
// Router context symbol
const ROUTER_CONTEXT = getSymbol('router-context');
const CURRENT_PAGE_MARKER = getSymbol('current-page-marker');
const CONTEXT_REQUEST_HANDLER = getSymbol('context-request-handler');
const PAGE_TRANSITION = getSymbol('page-transition');
const CREATED_AT = getSymbol('created-at');
// Lifecycle symbols
const READY_HANDLERS = getSymbol('ready-handlers');
const DISPOSE_HANDLERS = getSymbol('dispose-handlers');
const RECONNECT_HANDLERS = getSymbol('reconnect-handlers');
const MOVED_HANDLERS = getSymbol('moved-handlers');
const ADOPTED_HANDLERS = getSymbol('adopted-handlers');
// Observer symbols
const OBSERVERS = getSymbol('observers');
// Part symbols
const PARTS = getSymbol('parts');
const PART_TIMERS = getSymbol('part-timers');
// Property setter flag (prevents attributeChangedCallback re-triggering watchers)
const SETTING_FROM_PROPERTY = getSymbol('setting-from-property');
// Lifecycle callback timers
const MOVED_TIMERS = getSymbol('moved-timers');
const ADOPTED_TIMERS = getSymbol('adopted-timers');
// Dispatch timing symbols
const DISPATCH_TIMERS = getSymbol('dispatch-timers');
// Render symbols (v3.0.0)
const RENDER_METHOD = getSymbol('render-method');
const RENDER_OPTIONS = getSymbol('render-options');
const RENDER_INSTANCE = getSymbol('render-instance');
const RENDER_TIMERS = getSymbol('render-timers');
const RENDER_CALLBACKS = getSymbol('render-callbacks');
const STYLES_METHOD = getSymbol('styles-method');
const STYLES_APPLIED = getSymbol('styles-applied');
const PARENT_STYLES_METHODS = getSymbol('parent-styles-methods');
const SNICE_ELEMENT_BASE = getSymbol('snice-element-base');
// Navigation context symbols
const CONTEXT_HANDLER = getSymbol('context-handler');
const CONTEXT_METHOD_NAME = getSymbol('context-method-name');
const NAVIGATION_CONTEXT_INSTANCE = getSymbol('navigation-context-instance');
const REGISTERED_ELEMENTS = getSymbol('registered-elements');
const IS_UPDATING = getSymbol('is-updating');
const CONTEXT_REGISTER = getSymbol('context-register');
const CONTEXT_UNREGISTER = getSymbol('context-unregister');
const CONTEXT_NOTIFY_ELEMENT = getSymbol('context-notify-element');
const CONTEXT_OPTIONS = getSymbol('context-options');
const CONTEXT_TIMER = getSymbol('context-timer');
const CONTEXT_CALLED = getSymbol('context-called');
const CONTEXT_UPDATE = getSymbol('context-update');
// Registration de-dup sets on the constructor — replace previous `__*Methods`
// expando strings that violated the no-double-underscore rule.
const CONTEXT_METHODS = getSymbol('context-methods');
const WATCH_METHODS = getSymbol('watch-methods');
const OBSERVE_METHODS = getSymbol('observe-methods');
const ON_METHODS = getSymbol('on-methods');
const RESPOND_METHODS = getSymbol('respond-methods');
const READY_METHODS = getSymbol('ready-methods');
const DISPOSE_METHODS = getSymbol('dispose-methods');
const RECONNECT_METHODS = getSymbol('reconnect-methods');
const MOVED_METHODS = getSymbol('moved-methods');
const ADOPTED_METHODS = getSymbol('adopted-methods');
// Controller attach-abort — set to an AbortController while attachController
// is awaiting element.ready, so detachController can short-circuit the wait.
const CONTROLLER_ABORT = getSymbol('controller-abort');
// Set on a controller instance once it has gotten past element.ready and is
// being attached, so detachController knows not to run detach() on a controller
// whose attach() was aborted before it ever ran.
const CONTROLLER_ATTACHED = getSymbol('controller-attached');
const PROPERTY_DEFINERS = getSymbol('property-definers');

export { ADOPTED_HANDLERS, ADOPTED_METHODS, ADOPTED_TIMERS, CHANNEL_HANDLERS, CLEANUP, CONTEXT_CALLED, CONTEXT_HANDLER, CONTEXT_METHODS, CONTEXT_METHOD_NAME, CONTEXT_NOTIFY_ELEMENT, CONTEXT_OPTIONS, CONTEXT_REGISTER, CONTEXT_REQUEST_HANDLER, CONTEXT_TIMER, CONTEXT_UNREGISTER, CONTEXT_UPDATE, CONTROLLER, CONTROLLER_ABORT, CONTROLLER_ATTACHED, CONTROLLER_ATTRIBUTE_SYNC, CONTROLLER_ID, CONTROLLER_KEY, CONTROLLER_NAME_KEY, CONTROLLER_OPERATIONS, CONTROLLER_REGISTRY_NAME, CREATED_AT, CURRENT_PAGE_MARKER, DAEMON_STATE, DIRECT_CONTROLLER, DISPATCH_TIMERS, DISPOSE_HANDLERS, DISPOSE_METHODS, ELEMENT_OPTIONS, EXPLICITLY_SET_PROPERTIES, INITIALIZED, IS_CONTROLLER_CLASS, IS_CONTROLLER_INSTANCE, IS_DAEMON_CLASS, IS_DAEMON_INSTANCE, IS_ELEMENT_CLASS, IS_UPDATING, MOVED_HANDLERS, MOVED_METHODS, MOVED_TIMERS, NATIVE_CONTROLLER, NAVIGATION_CONTEXT_INSTANCE, OBSERVERS, OBSERVE_METHODS, ON_HANDLERS, ON_METHODS, PAGE_TRANSITION, PARENT_STYLES_METHODS, PARTS, PART_TIMERS, PENDING_CONTROLLER_BINDING, PENDING_RECONNECT_RENDER, PRE_INIT_PROPERTY_VALUES, PRE_UPGRADE_PROPERTY_BINDINGS, PROPERTIES, PROPERTIES_INITIALIZED, PROPERTY_DEFAULTS, PROPERTY_DEFINERS, PROPERTY_VALUES, PROPERTY_WATCHERS, PROPERTY_WRAPPERS, READY_HANDLERS, READY_HANDLERS_RUNNING, READY_METHODS, READY_PROMISE, READY_REJECT, READY_RESOLVE, RECONNECT_HANDLERS, RECONNECT_METHODS, REGISTERED_ELEMENTS, RENDERED_PROMISE, RENDERED_RESOLVE, RENDER_CALLBACKS, RENDER_DEPTH, RENDER_INSTANCE, RENDER_METHOD, RENDER_OPTIONS, RENDER_ROOT, RENDER_TIMERS, RESPOND_METHODS, ROUTER_CONTEXT, SETTING_FROM_PROPERTY, SNICE_ELEMENT_BASE, STYLES_APPLIED, STYLES_METHOD, WATCH_METHODS, getSymbol };
//# sourceMappingURL=symbols.esm.js.map
